// v32 — zdieľanie štatistík s priateľmi: v Prehľade si používateľ vyberie jednu alebo viac
// dlaždíc (režim „Zdieľať") a tu zvolí komu — priatelia cez zdieľaný FriendChip picker
// (rovnaký element ako pozvánky do výzvy), plus existujúce skupiny. Každá štatistika sa
// odošle ako samostatná štruktúrovaná správa (kind='stat' + payload: ikona, číslo, názov)
// a chat ju vykreslí ako dlaždicu.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { BuzerIcon } from "../../BuzerIcons";
import { st } from "../../styles.js";
import { FriendChip, FriendPickerHint } from "./FriendChip";
import { Glyph } from "./Glyph";
import { Button, Sheet } from "./ds";

// mini dlaždica štatistiky (rovnaká v náhľade aj v chate): ikona + číslo + názov, bez popisu
export function StatMiniTile({ stat, size = "md", style }) {
  const big = size === "md";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: big ? 12 : 10, background: "var(--surface-solid)", border: "1px solid var(--border)", borderRadius: 14, padding: big ? "12px 14px" : "10px 12px", minWidth: 0, ...style }}>
      <span style={{ width: big ? 44 : 38, height: big ? 44 : 38, borderRadius: 999, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: stat.accent || "var(--brand-primary)", color: "var(--on-solid)" }}>
        <Glyph icon={stat.icon} size={big ? 22 : 20} fallback="chart" />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: big ? 24 : 20, lineHeight: 1.1, color: "var(--text)" }}>{stat.value}</span>
        <span style={{ display: "block", fontSize: 10.5, fontFamily: "var(--font-mono)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--neutral-text-muted)" }}>{stat.label}</span>
      </span>
    </div>
  );
}

export function ShareStatSheet({ stats, onClose }) {
  // stats = [{ icon, label, value, accent }] — jedna alebo viac vybraných dlaždíc
  const [friends, setFriends] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selFriends, setSelFriends] = useState([]);
  const [selGroups, setSelGroups] = useState([]);
  const [text, setText] = useState("Chcem sa ti pochváliť 🎉");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => setFriends([]));
    api("/api/conversations").then((d) => setGroups((d.conversations || []).filter((c) => c.is_group))).catch(() => {});
  }, []);
  const toggleFriend = (id) => setSelFriends((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleGroup = (id) => setSelGroups((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const total = selFriends.length + selGroups.length;
  const payloads = stats.map((s) => ({ icon: s.icon, label: s.label, value: String(s.value), accent: s.accent || "" }));

  async function send() {
    if (!total || busy) return;
    setBusy(true); setErr("");
    try {
      const body = text.trim() || "Chcem sa ti pochváliť 🎉";
      // v32 — všetky vybrané štatistiky idú v JEDNEJ správe (payload.stats) s jedným textom
      const payload = payloads.length === 1 ? payloads[0] : { stats: payloads };
      const sendAll = (cid) => api("/api/conversations/" + cid + "/messages", { method: "POST", body: { body, kind: "stat", payload } });
      // každému priateľovi zvlášť do 1:1 chatu; skupinám priamo
      for (const fid of selFriends) {
        const c = await api("/api/conversations", { method: "POST", body: { member_ids: [fid] } });
        await sendAll(c.id);
      }
      for (const cid of selGroups) await sendAll(cid);
      setSent(true);
      setTimeout(onClose, 900);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <Sheet center title={stats.length > 1 ? `Zdieľať štatistiky (${stats.length})` : "Zdieľať štatistiku"} icon="share" onClose={onClose}
      footer={
        <Button variant="primary" block disabled={!total || busy} onClick={send}>
          {sent ? <><BuzerIcon name="check" size={16} color="currentColor" strokeWidth={2.6} />Odoslané</>
            : busy ? "…" : total > 0 ? `Odoslať do chatu (${total})` : "Odoslať do chatu"}
        </Button>
      }>
      {/* náhľad dlaždíc — rovnaké prídu do chatu (len ikona + číslo + názov) */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {stats.map((s, i) => <StatMiniTile key={i} stat={s} />)}
      </div>
      <div style={{ ...st.fieldHint, margin: "12px 0 6px" }}>Text správy</div>
      <input className="field" style={{ ...st.field, width: "100%" }} maxLength={200}
        value={text} onChange={(e) => setText(e.target.value)} />

      <div style={{ ...st.fieldHint, margin: "14px 0 6px" }}>Komu (každému zvlášť do chatu)</div>
      {friends === null ? <div style={{ display: "flex", justifyContent: "center", padding: 14 }}><div className="spin" /></div>
        : friends.length === 0 ? <div style={st.taskDesc}>Zatiaľ nemáš priateľov — pridaj si ich v záložke Priatelia.</div>
          : (<>
            {/* v32 — rovnaký friend picker ako pozvánky do výzvy (FriendChip + hint) */}
            <FriendPickerHint />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 200, overflowY: "auto" }}>
              {friends.map((f) => (
                <FriendChip key={f.friend_id} friend={f} selected={selFriends.includes(f.friend_id)} onToggle={toggleFriend}
                  chipStyle={{ borderRadius: "var(--radius-pill)", padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                    background: "var(--neutral-surface-2)", color: "var(--neutral-text-main)", border: "1.5px solid var(--neutral-border-strong)" }}
                  chipOnStyle={{ background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }} />
              ))}
            </div>
          </>)}
      {groups.length > 0 && (<>
        <div style={{ ...st.fieldHint, margin: "14px 0 6px" }}>Alebo do skupiny</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {groups.map((g) => {
            const on = selGroups.includes(g.id);
            return (
              <button key={g.id} type="button" onClick={() => toggleGroup(g.id)} className="chip"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: "var(--radius-pill)", padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                  ...(on ? { background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }
                    : { background: "var(--neutral-surface-2)", color: "var(--neutral-text-main)", border: "1.5px solid var(--neutral-border-strong)" }) }}>
                <Glyph icon="people" size={14} />{g.name || "Skupina"}
              </button>
            );
          })}
        </div>
      </>)}
      {err && <div style={{ ...st.err, marginTop: 10 }}>{err}</div>}
    </Sheet>
  );
}
